import { makeAutoObservable } from 'mobx';
import CompanyDocument from 'main/stores/documents/CompanyDocument';
import Signature from 'main/stores/signatures/Signature';
import {api, t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import Folder from 'main/stores/folders/Folder';
import {DomainStore, fetchModels} from 'shared/store';
import {successAlert} from 'shared/tools';
import _ from 'lodash';
import TaxDocumentAssignment from 'main/stores/documents/TaxDocumentAssignment';
import Employee from 'main/stores/employees/Employee';

class CompanyDocumentDirectoryState {
  interactiveAgent;
  history;
  companyDocumentStore;

  employees = [];
  signatures = [];
  folders = [];
  reorderingDocuments = [];
  reorderFolder = null;
  fetchingReorderingDocuments = false;
  interactiveContextState = {};
  editingSignature = new Signature();
  editingDocument;
  archivingDocument;
  originalSignatureData;
  addDocumentModalOpen = false;
  archiveDocumentModalOpen = false;
  unarchiveDocumentModalOpen = false;
  editSignatureModalOpen = false;
  addAssigneeModalOpen = false;
  taxDocumentsModalOpen = false;
  sendFederalTaxForm = false;
  sendProvincialTaxForm = false;
  sendAllEmployees = false;
  taxFormDueDate = null;
  errors = {};
  uploadMethod = false;
  processing = false;

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  constructor(companyDocumentStore, signatureStore, history) {
    makeAutoObservable(this, {
      interactiveAgent: false,
      history: false,
      companyDocumentStore: false
    });

    this.companyDocumentStore = companyDocumentStore;
    this.signatureStore = signatureStore;
    this.history = history;
  }

  async load() {
    this.employees = this.companyDocumentStore.getAllOrdered(types.EMPLOYEE, Employee);
    this.folders = _.map(
      await fetchModels(endpoints.FOLDERS.ALL, types.FOLDER),
      folder => new Folder(folder)
    );
    await this.refreshSignatures();
  }

  updateInteractiveContextState(interactiveContextState) {
    this.interactiveContextState = interactiveContextState;
  }

  get canReorderDocuments() {
    return this.interactiveContextState.filter?.get('status') !== 'archived';
  }

  async openReorderDocumentsModal(folder = null) {
    await this.selectReorderFolder(folder);
  }

  async selectReorderFolder(folder) {
    this.reorderFolder = folder;
    this.fetchingReorderingDocuments = true;

    this.reorderingDocuments = _.map(
        await fetchModels(
            endpoints.COMPANY_DOCUMENTS.activeWithFolder(folder?.id),
            types.COMPANY_DOCUMENT
        ),
        document => new CompanyDocument(document)
    )
    this.fetchingReorderingDocuments = false;
  }

  async saveDocumentOrders() {
    const documentParams = {};
    this.reorderingDocuments.forEach(document => documentParams[document.id] = document.order);

    await api.patch(endpoints.COMPANY_DOCUMENTS.REORDER, {
      company_documents: documentParams,
      folder_id: this.reorderFolder?.id
    });

    this.closeReorderDocumentsModal();
    this.interactiveAgent.refresh();
    this.interactiveAgent.refreshFolders();
  }

  async fetchFolderDocuments(folder, filter) {
    const store = new DomainStore();
    const endpointsToLoad = [endpoints.COMPANY_DOCUMENTS.withFolderAndFilter(folder.id, filter)];

    if (folder.hasSubfolders) {
      endpointsToLoad.push(endpoints.FOLDERS.withFilterAndParent(filter, folder.id));
    }

    await store._compose(endpointsToLoad);

    folder.companyDocuments = store._getAll(types.COMPANY_DOCUMENT, CompanyDocument);

    if (folder.hasSubfolders) {
      folder.subfolders = store._getAll(types.FOLDER, Folder);
    }
  }

  openAddDocumentModal() {
    this.processing = false;
    this.addDocumentModalOpen = true;
  }

  goToEditDocument(document) {
    this.history.push(`/documents/edit/${document.id}`);
  }

  closeAddDocumentModal() {
    this.addDocumentModalOpen = false;
    this.errors = {};
    this.uploadMethod = false;
  }

  openArchiveDocumentModal(model) {
    this.archiveDocumentModalOpen = true;
    this.archivingDocument = model;
  }

  closeArchiveDocumentModal() {
    this.archiveDocumentModalOpen = false;
    this.archivingDocument = null;
  }

  openUnarchiveDocumentModal(model) {
    this.unarchiveDocumentModalOpen = true;
    this.unarchivingDocument = model;
  }

  closeUnarchiveDocumentModal() {
    this.unarchiveDocumentModalOpen = false;
    this.unarchivingDocument = null;
  }

  async removeCompanyDocument(model) {
    await this.companyDocumentStore.destroy(model);
    this.interactiveAgent.refreshFolders();
  }

  async archiveCompanyDocument() {
    const {model} = await this.companyDocumentStore.post(
      this.archivingDocument.link('archive'),
      types.COMPANY_DOCUMENT
    );

    if (model) {
      this.interactiveAgent.refresh();
      this.interactiveAgent.refreshFolders();
      this.closeArchiveDocumentModal();
    }
  }

  async unarchiveCompanyDocument() {
    const {model} = await this.companyDocumentStore.post(
      this.unarchivingDocument.link('unarchive'),
      types.COMPANY_DOCUMENT
    );

    if (model) {
      this.interactiveAgent.refresh();
      this.interactiveAgent.refreshFolders();
      this.closeUnarchiveDocumentModal();
    }
  }

  uploadExistingDocuments() {
    this.uploadMethod = true;
  }

  async createNewCompanyDocument() {
    const companyDocument = new CompanyDocument({documentType: 'rich_text_template'});
    this.processing = true;
    const {model} = await this.companyDocumentStore.postCompanyDocument(companyDocument);
    this.goToEdit(model.id);
  }

  goToEdit(id) {
    this.history.push(`/documents/edit/${id}`);
  }

  openEditSignatureModal(signature) {
    this.editSignatureModalOpen = true;

    if (signature) {
      this.editingSignature = new Signature(signature);
      this.originalSignatureData = signature.signatureData;
    } else {
      this.editingSignature = new Signature();
      this.originalSignatureData = null;
    }
  }

  closeEditSignatureModal() {
    this.editSignatureModalOpen = false;
    this.errors = {};
  }

  async removeSignature(signature) {
    await this.signatureStore.destroy(signature);
    this.signatures.remove(signature);
  }

  async saveSignature() {
    const {model, errors} = this.editingSignature.isNew
      ? await this.signatureStore.postSignature(this.editingSignature)
      : await this.signatureStore.patch(this.editingSignature);
    this.errors = errors;

    if (model) {
      this.refreshSignatures(model);
      this.closeEditSignatureModal();
      successAlert(t('company_settings.documents.alerts.Signature successfully saved.'));
    }
  }

  async sendTaxDocuments() {
    const {model, errors} = await this.companyDocumentStore.put(
      endpoints.TAX_DOCUMENTS,
      types.TAX_DOCUMENT_ASSIGNMENT,
      new TaxDocumentAssignment({
        employees: this.employees,
        sendFederalTaxForm: this.sendFederalTaxForm,
        sendProvincialTaxForm: this.sendProvincialTaxForm,
        dueDate: this.taxFormDueDate
      })
    );

    this.errors = errors;

    if (model) {
      this.closeTaxDocumentsModal();
      successAlert(t('company_settings.documents.alerts.Documents successfully sent.'));
    }
  }

  async refreshSignatures() {
    this.signatures = await this.signatureStore.loadSignatures();
  }

  openAddAssigneeModal(viewModel) {
    this.editingDocument = viewModel;
    this.addAssigneeModalOpen = true;
  }

  openTaxDocumentsModal() {
    this.clearTaxDocumentModal();
    this.taxDocumentsModalOpen = true;
  }

  closeTaxDocumentsModal() {
    this.taxDocumentsModalOpen = false;
    this.clearTaxDocumentModal();
    this.errors = {};
  }

  clearTaxDocumentModal() {
    this.employees = [];
    this.taxFormDueDate = null;
    this.sendFederalTaxForm = false;
    this.sendProvincialTaxForm = false;
  }

  closeAddAssigneeModal() {
    this.editingDocument = null;
    this.addAssigneeModalOpen = false;
  }

  changeSelectedEmployees(employees) {
    this.employees = employees;
  }

  onAssigneesAdded() {
    this.closeAddAssigneeModal();
    successAlert(t('company_settings.documents.alerts.Document successfully assigned.'));
  }

  get filterOptions() {
    return [
      {
        id: 'active',
        display: t('company_settings.documents.Active')
      },
      {
        id: 'archived',
        display: t('company_settings.documents.Archived')
      }
    ];
  }

  customFolderLinksFor(folder) {
    if (!this.canReorderDocuments || !folder.hasDocuments) return [];

    return [
      {
        order: 0,
        text: 'company_settings.documents.Reorder Documents',
        action: folder => this.openReorderDocumentsModal(folder)
      }
    ];
  }

  customLinksFor(model) {
    const links = [];

    if (model.hasLink('archive')) {
      links.push({
        order: 3,
        text: 'company_settings.documents.Archive',
        action: model => this.openArchiveDocumentModal(model)
      });
    }
    if (model.hasLink('unarchive')) {
      links.push({
        order: 0,
        text: 'company_settings.documents.Unarchive',
        action: model => this.openUnarchiveDocumentModal(model)
      });
    }
    if (model.hasLink('addAssignees')) {
      links.push({
        order: 2,
        text: 'company_settings.documents.Add Assignees',
        action: model => this.openAddAssigneeModal(model)
      });
    }

    links.push({
      order: 1,
      text: 'company_settings.documents.View Assignees',
      action: `/documents/:id/actions`
    });

    return links;
  }
}

export default CompanyDocumentDirectoryState;
